import React, { useState } from 'react';
import { Nav } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import SidebarNavItem from './SidebarNavItem';
import { useLocation } from 'react-router-dom';
import { HomeOutlined, SearchOutlined, FundOutlined, BarsOutlined, FileSearchOutlined, CommentOutlined, MailOutlined, CaretRightOutlined, EyeOutlined, SunOutlined, FundViewOutlined } from '@ant-design/icons';
import {useSubscription} from "../subscription/useSubscription";
import useHankoAuth from '../../hooks/useHankoAuth';
import {AppFeature} from "../subscription/AppFeatures";
import { Button, Grid } from 'antd';
import SubscriptionPlanModal from '../subscription/SubscriptionPlanModal';
import MarketDataUpsellModal from '../subscription/MarketDataUpsellModal';
import { createUpgradeContext } from '../subscription/upgradeContext';
import ContactUsModal from '../common/ContactUsModal';
import InvoiceOrderModal from '../billing/InvoiceOrderModal';
import DemoModal from '../demo/DemoModal';
import HeroDemo from '../demo/HeroDemo';
import TopNavIcons from './TopNavIcons';
import './Sidebar.css';

const HUBSPOT_DEMO_URL = 'https://cal.com/shady-el-tokhy-sl58vi/tendera?utm_source=app&utm_medium=upsell&utm_content=sidebar&metadata[ch]=appi';



const Sidebar = ({ isCollapsed, toggleSidebar, isMobileDrawer = false, onNavigate }) => {
    const { t } = useTranslation();
    // The product tour in the interface language; not on phones, where its text is too small to read.
    const screens = Grid.useBreakpoint();
    const proDemoVideoEnabled = Boolean(screens.md);
    const {hasFeature, plan} = useSubscription();
    const isFreePlan = plan?.plan?.planType === 'FREE';
    // The badge names the actual tier (Monitoring / Pro / Intelligence) - planType only says
    // FREE/TRIAL/PAID, so every paid tier used to read "PRO". The name is shown on its own, with no
    // "-TILAUS"/"PLAN" suffix: backend plan names are free text ("Pro (Legacy)", "Tendera FREE") and
    // a suffix glued onto them reads wrong in fi/sv. The green dot and border already say it is an
    // active subscription.
    const planName = plan?.plan?.name;
    const { user } = useHankoAuth();
    const isAdmin = user?.email?.endsWith('@tendera.bid') ?? false;
    const canUseEye = isAdmin || hasFeature(AppFeature.EYE_AI_CHAT);
    // Pro gets the Analytics hub without the Eye chat; Eye implies it.
    const canUseAnalytics = canUseEye || hasFeature(AppFeature.MARKET_ANALYTICS);
    // Silma Autopilot: admin-only (plus org overrides) until the products are released.
    const canUseDailyBrief = isAdmin || hasFeature(AppFeature.DAILY_BRIEF);
    const canUseBidLedger = isAdmin || hasFeature(AppFeature.BID_LEDGER);
    const canUseNewsletter = isAdmin || hasFeature(AppFeature.MARKET_DATA_NEWSLETTER);
    const sidebarWidth = isCollapsed ? '60px' : '250px';
    const [isMarketDataModalOpen, setIsMarketDataModalOpen] = useState(false);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [planModalContext, setPlanModalContext] = useState(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isProDemoVideoOpen, setIsProDemoVideoOpen] = useState(false);

    const handleBookDemo = () => {
        window.open(HUBSPOT_DEMO_URL, '_blank', 'noopener,noreferrer');
    };

    const handleMarketDataClick = () => {
        setIsMarketDataModalOpen(true);
    };

    const handleCloseMarketData = () => {
        setIsMarketDataModalOpen(false);
    };

    const handleOpenPlanModal = (source = 'sidebar_market_data', details = {}) => {
        setIsMarketDataModalOpen(false);
        setPlanModalContext(createUpgradeContext(source, details));
        setIsPlanModalOpen(true);
    };



    const desktopStyle = {
        width: sidebarWidth,
        backgroundColor: '#27343A',
        color: '#FFFFFF',
        borderRight: '1px solid #1f2a2f',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        // Below every antd overlay (Drawer/Modal masks start at 1000) — with a
        // higher value, maximized drawers and modals slid UNDER the nav. Still
        // above regular page content (z-index 1–2 range).
        zIndex: 900,
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column'
    };

    const mobileDrawerStyle = {
        width: '100%',
        height: '100%',
        backgroundColor: '#27343A',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column'
    };

    return (
        <div style={isMobileDrawer ? mobileDrawerStyle : desktopStyle}>
            {/* Logo / Brand Area */}
            <div
                style={{
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: (!isMobileDrawer && isCollapsed) ? 'center' : 'space-between',
                    padding: (!isMobileDrawer && isCollapsed) ? '0' : '0 1rem',
                    transition: 'all 0.3s ease'
                }}
            >
                <Logo isCollapsed={!isMobileDrawer && isCollapsed} />
                {isMobileDrawer && (
                    <button
                        onClick={onNavigate}
                        aria-label="Close navigation menu"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#FFFFFF',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <i className="fas fa-times" />
                    </button>
                )}
            </div>

            {/* Toggle Button — desktop only */}
            {!isMobileDrawer && (
                <button
                    onClick={toggleSidebar}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '-10px',
                        zIndex: 950,
                        backgroundColor: '#FFFFFF',
                        border: '2px solid #27343A',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                    }}
                >
                    <i
                        className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'}`}
                        style={{ color: '#27343A', fontSize: '10px' }}
                    />
                </button>
            )}

            {/* Navigation */}
            <div style={{ padding: '1rem', flex: 1 }}>
                <Nav vertical>
                    <SidebarNavItem
                        to="/"
                        icon={HomeOutlined}
                        label={t('navigation.home')}
                        isCollapsed={!isMobileDrawer && isCollapsed}
                        onNavigate={onNavigate}
                    />
                    <SidebarNavItem
                        to="/search"
                        icon={SearchOutlined}
                        label={t('navigation.search')}
                        isCollapsed={!isMobileDrawer && isCollapsed}
                        onNavigate={onNavigate}
                    />
                    <SidebarNavItem
                        to="/my-profiles"
                        icon={FundOutlined}
                        label={t('navigation.myProfiles')}
                        isCollapsed={!isMobileDrawer && isCollapsed}
                        onNavigate={onNavigate}
                    />
                    <SidebarNavItem
                        to="/mybids"
                        icon={BarsOutlined}
                        label={t('navigation.myBids')}
                        isCollapsed={!isMobileDrawer && isCollapsed}
                        onNavigate={onNavigate}
                    />
                    {canUseNewsletter && <SidebarNavItem to="/weekly-newsletter" icon={MailOutlined}
                        label={t('newsletter.title')} isCollapsed={!isMobileDrawer && isCollapsed} onNavigate={onNavigate} />}
                    {canUseDailyBrief && (
                        <SidebarNavItem
                            to="/daily-brief"
                            icon={SunOutlined}
                            label={t('navigation.dailyBrief', 'Aamukatsaus')}
                            isCollapsed={!isMobileDrawer && isCollapsed}
                            onNavigate={onNavigate}
                        />
                    )}
                    {canUseBidLedger && (
                        <SidebarNavItem
                            to="/bid-ledger"
                            icon={FundViewOutlined}
                            label={t('navigation.bidLedger', 'Menetetyt kaupat')}
                            isCollapsed={!isMobileDrawer && isCollapsed}
                            onNavigate={onNavigate}
                        />
                    )}
                    {canUseAnalytics && (
                        <SidebarNavItem
                            to="/market-intel"
                            icon={EyeOutlined}
                            label={t('navigation.marketIntel', 'Analytiikka')}
                            isCollapsed={!isMobileDrawer && isCollapsed}
                            onNavigate={onNavigate}
                        />
                    )}
                    {canUseEye && (
                        <SidebarNavItem
                            to="/decisions/chat"
                            icon={CommentOutlined}
                            label={t('navigation.eye', 'Eye')}
                            isCollapsed={!isMobileDrawer && isCollapsed}
                            onNavigate={onNavigate}
                        />
                    )}
                    {hasFeature(AppFeature.DECISIONS) ? (
                        <>
                            <SidebarNavItem
                                to="/decisions"
                                excludePaths={['/decisions/chat']}
                                icon={FileSearchOutlined}
                                label={t('navigation.decisions')}
                                isCollapsed={!isMobileDrawer && isCollapsed}
                                onNavigate={onNavigate}
                            />
                        </>
                    ) : (
                        <SidebarNavItem
                            to="/decisions"
                                excludePaths={['/decisions/chat']}
                            icon={FileSearchOutlined}
                            label={t('navigation.decisions')}
                            isCollapsed={!isMobileDrawer && isCollapsed}
                            disabled
                            onClick={handleMarketDataClick}
                        />
                    )}
                </Nav>
            </div>
            {(!isCollapsed || isMobileDrawer) && (
                <div className="sidebar-bottom">
                    <hr className="sidebar-bottom-divider" />
                    <button
                        type="button"
                        className="sidebar-contact-link"
                        onClick={() => setIsContactModalOpen(true)}
                    >
                        <MailOutlined className="sidebar-contact-icon" />
                        <span>{t('sidebar.contactUs', 'Ota yhteyttä')}</span>
                    </button>
                    {isFreePlan ? (
                        <div className="sidebar-upsell-box">
                            <div className="sidebar-upsell-status">
                                <span className="sidebar-upsell-status-label">
                                    {t('sidebar.currentPlan', 'NYKYINEN TILAUS:')}
                                </span>
                                <span className="sidebar-upsell-status-badge">FREE</span>
                            </div>
                            <div className="sidebar-upsell-heading">
                                {t('sidebar.unlockProHeading', 'Avaa PRO:n kaikki ominaisuudet')}
                            </div>
                            {proDemoVideoEnabled && (
                                <button
                                    type="button"
                                    className="sidebar-upsell-demo-link"
                                    onClick={() => setIsProDemoVideoOpen(true)}
                                >
                                    <CaretRightOutlined className="sidebar-upsell-demo-caret" />
                                    <span className="sidebar-upsell-demo-text">
                                        {t('sidebar.watchProDemo', 'Katso PRO-demo')}
                                    </span>
                                    <span className="sidebar-upsell-demo-duration">
                                        {t('sidebar.watchProDemoDuration', '(1 min)')}
                                    </span>
                                </button>
                            )}
                            <hr className="sidebar-upsell-divider" />
                            <Button
                                type="primary"
                                block
                                className="sidebar-upsell-primary"
                                onClick={() => handleOpenPlanModal('sidebar_pricing')}
                            >
                                {t('sidebar.pricing', 'Hinnasto')}
                            </Button>
                            <Button
                                block
                                className="sidebar-upsell-secondary"
                                onClick={handleBookDemo}
                            >
                                {t('sidebar.bookDemo', 'Varaa esittely')}
                            </Button>
                        </div>
                    ) : (
                        <div className="sidebar-pro-badge">
                            <span className="sidebar-pro-badge-dot" />
                            {planName
                                ? planName.toUpperCase()
                                : t('sidebar.proPlanBadge', 'PRO-TILAUS')}
                        </div>
                    )}
                </div>
            )}
            {isMobileDrawer && (
                <div style={{ borderTop: '1px solid #3d5159', padding: '0.75rem 1rem' }}>
                    <TopNavIcons isMobile />
                </div>
            )}
            <SubscriptionPlanModal
                open={isPlanModalOpen}
                onClose={() => setIsPlanModalOpen(false)}
                hideFreePlan
                upgradeContext={planModalContext}
            />
            <MarketDataUpsellModal open={isMarketDataModalOpen} onClose={handleCloseMarketData} />
            <ContactUsModal open={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
            <InvoiceOrderModal />
            {proDemoVideoEnabled && (
                <DemoModal open={isProDemoVideoOpen} onClose={() => setIsProDemoVideoOpen(false)}>
                    <HeroDemo />
                </DemoModal>
            )}
        </div>
    );
};

export default Sidebar;
